import React, { useEffect, useState } from "react";

import { getLabelFromTechName } from "../../Newsletter/NewsletterSteps/ThirdStep";
import { getConnectionData } from "../../../util/Scripts";
import { useEffectPlus } from "../../../util/customHooks";
import { getListV2 } from "../../../services/api/Api";
import { useIntl } from "react-intl";
import { Spin } from "../../BP2Components";
import {
   aggregateUsersByDate,
   aggregateUsersByNumber,
   calculateMinMaxValues,
   fillMissingUsers,
   isDateInName,
   removeHours,
   sortListByDate,
   sumRatingValues,
   fillUndefinedWithRandomNumbers,
   getRandomColor,
   checkIfWidgetHaveDataToRender
} from "../WidgetsSettings/lineChartSettings/LineChartScripts";
import {
   LineChart,
   Line,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   Legend,
   ReferenceLine,
   Label
} from "recharts";

import Widget from "../../Widget/Widget";
import SettingsModal from "../../Modal/SettingsModal";
import LineChartModal from "../WidgetsSettings/lineChartSettings/LineChartModal";

import "../WidgetsSettings/lineChartSettings/LineChartStyle.less";

// 🛑 ATTENTION 🛑

// if you are confused by anything, go to lineChartREADME.txt

// 🛑 ATTENTION 🛑
interface LineChartInterface {
   value: any;
   role: any;
}
type WidgetData = {
   name: string;
   [key: string]: any;
};

interface LineChartItem {
   label: string | number;
   value: string;
   userColor: string;
}

const LineChartWidget: React.FC<LineChartInterface> = ({ value, role }) => {
   const sumRatings = value?.sumRatings ? value?.sumRatings : false;

   const [loading, setLoading] = useState(true);
   const [axisYValues, setAxisYValues] = useState({ minYValue: 0, maxYValue: 10 });
   const [pageToDownload, setPageToDownload] = useState(1);
   const [downloadedItemsLength, setDownloadedItemsLength] = useState(0);
   const [totalItemsCount, setTotalItemsCount] = useState<number | null>(0);
   const [chartList, setChartList] = useState<WidgetData[]>([]);
   const [sumedWidgetDataList, setSumedWidgetDataList] = useState<WidgetData[]>([]);
   const [listResponse, setListResponse] = useState();
   const [listResponseData, setListResponseData] = useState([]);
   const [itemFromConnection, setItemFromConnection] = useState({});
   const [itemsList, setItemsList] = useState<Array<LineChartItem>>([]);

   const width = window.innerWidth;
   const intl = useIntl();

   const uniqueUsers = itemsList?.map((user) => user.label);

   const connectionsData = getConnectionData(value.id);

   // Okay folks, here we handle connections for lineChart, you can read more in LineChartReadme.txt
   useEffect(() => {
      if (!connectionsData) {
         setItemsList(value?.users);
      }
   }, [value?.users]);

   useEffect(() => {
      if (connectionsData) {
         // here we getting value, by extracting field name (techName) from connection, then we are getting value form that fild (field can be different, thats why is so complicated)
         const valueForConnection = connectionsData[Object.keys(connectionsData)[0]];
         const connectionTechName = Object.keys(connectionsData)[0];

         // getting label
         const labelForConnection = getLabelFromTechName(
            connectionTechName,
            //@ts-ignore TODO: make list response interface
            listResponse?.data?.default?.columns
         );

         // checking if data list contains field named: {labelForConnection}
         if (listResponseData?.every((object) => labelForConnection in object)) {
            // finding object with id by connection value
            const objectWithConnectionItem = listResponseData?.find(
               //@ts-ignore TODO: make list response interface
               (obj) => obj?.[labelForConnection]?.value === valueForConnection
            );
            // creating item object we put in chart. By that user, all data will be filtering
            if (objectWithConnectionItem) {
               const connectionUser: LineChartItem = {
                  label: valueForConnection,
                  //@ts-ignore
                  value: objectWithConnectionItem[labelForConnection]?.id,
                  userColor: getRandomColor()
               };

               // pushing it in redux (if user is not in state already)
               if (
                  itemsList !== null &&
                  itemsList !== undefined &&
                  Object.keys(itemFromConnection).length === 0
               ) {
                  setItemsList([connectionUser]);
                  setItemFromConnection(connectionUser);
               }
            }
         }
      }

      // fetching lists
      try {
         if (
            (value?.listId && totalItemsCount === 0) ||
            totalItemsCount !== downloadedItemsLength
         ) {
            setLoading(true);
            //TODO: spradzić jak to działa z sort z php - nie działa na backu, jak zacznie to sprawdzić xD
            getListV2(value.listId, pageToDownload, 200).then((res) => {
               if (totalItemsCount !== res?.data?.default?.meta?.totalItemsCount) {
                  setTotalItemsCount(res?.data?.default?.meta?.totalItemsCount);
               }
               setDownloadedItemsLength(downloadedItemsLength + res?.data?.default?.data?.length);
               setPageToDownload(pageToDownload + 1);
               setListResponse(res);
               if (listResponseData.length === 0) {
                  // if listResponse is empty we are just settings list
                  setListResponseData(res?.data?.default?.data);
               } else {
                  // if list is not empty, we joining old list with new one
                  setListResponseData(listResponseData.concat(res?.data?.default?.data));
               }
               setLoading(false);
            });
         } else {
            setLoading(true);
         }
      } catch (err) {
         console.log(err);
      }
   }, [value?.listId, window.location.search, pageToDownload, totalItemsCount]);

   // this useEffect handle suming values
   useEffect(() => {
      if (sumRatings) {
         // same here: if name is date - we are dealing with converting data and additionaly we suming values for user
         if (isDateInName(chartList)) {
            setSumedWidgetDataList(
               sortListByDate(aggregateUsersByDate(removeHours(sumRatingValues(chartList))))
            );
         } else {
            // if name is number, we joing users by the same number, and suming values of each one
            setSumedWidgetDataList(aggregateUsersByNumber(sumRatingValues(chartList)));
         }
      }
   }, [chartList, sumRatings]);

   // in this useEffect we are dealing with transforming request data to chart list
   useEffect(() => {
      const transformResponseToChartList = (
         response,
         columnXTechName,
         userIdTechName,
         ratingTechName,
         listResponse
      ) => {
         if (
            response &&
            columnXTechName &&
            userIdTechName &&
            ratingTechName &&
            listResponse &&
            value?.users.length > 0
         ) {
            // getting labels from columns
            const columnXTechNameLabel = listResponse.data?.default.columns.find(
               (column) => column?.options?.techName === columnXTechName
            )?.label;
            const userIdLabel = listResponse.data?.default.columns.find(
               (column) => column?.options?.techName === userIdTechName
            )?.label;
            const ratingLabel = listResponse.data?.default.columns.find(
               (column) => column?.options?.techName === ratingTechName
            )?.label;

            // creating list with fields required by LineChart
            [];

            const initialChartList = value?.users
               ?.flatMap((user) => {
                  return response?.map((elem) => {
                     //@ts-ignore
                     if (parseInt(elem[userIdLabel]?.id) === parseInt(user.value)) {
                        return {
                           name: elem[columnXTechNameLabel],
                           [user.label]: elem[ratingLabel]?.value
                              ? elem[ratingLabel]?.value
                              : elem[ratingLabel]
                        };
                     }
                     return null;
                  });
               })
               .filter((obj) => obj !== null);

            // creating copy of the list
            const chartListCopy = [...initialChartList];

            // filtering list by user, to prevend showing data of other users
            const filteredChartList = chartListCopy.filter((item) =>
               value?.users?.some((user) => item.hasOwnProperty(user.label))
            );

            if (chartList.length > 0) {
               // if chartList already have some data (for example when we downloading many pages), we joining old list with new.
               const _ = require("lodash");
               const combinedChartLists = filteredChartList.concat(chartList);

               // checking if X axis is date, if so, we sorting it (from oldest to newest), aggregating users (joing users with the same date, to one object), and removing time.
               if (isDateInName(combinedChartLists)) {
                  setChartList(
                     sortListByDate(aggregateUsersByDate(removeHours(combinedChartLists)))
                  );
               } else {
                  // if no, we are aggreagating users by the same number (joining by the same number in "name" field)
                  setChartList(aggregateUsersByNumber(combinedChartLists));
               }
               // here we are setting biggest and smallest Y axis values (when changing data in settings, for example users, old Y axis values are still present without it )
               setAxisYValues(calculateMinMaxValues(combinedChartLists, value));
            } else {
               if (isDateInName(filteredChartList)) {
                  setChartList(
                     sortListByDate(aggregateUsersByDate(removeHours(filteredChartList)))
                  );
               } else {
                  setChartList(filteredChartList);
               }
               setAxisYValues(calculateMinMaxValues(filteredChartList, value));
            }
         }
      };

      if (
         listResponseData &&
         value?.columnXTechName &&
         listResponse &&
         value?.userTechName &&
         value?.ratingColumnTechName
      ) {
         transformResponseToChartList(
            listResponseData,
            value?.columnXTechName,
            value?.userTechName,
            value?.ratingColumnTechName,
            listResponse
         );
      }
   }, [listResponse, listResponseData, value]);

   fillMissingUsers(chartList, uniqueUsers, sumRatings, sumedWidgetDataList);

   return (
      <Widget
         title={value?.title}
         role={role}
         extra={
            (role === "OWNER" || role === "MODERATOR") && (
               //@ts-ignore
               <SettingsModal
                  widgetName={value.name}
                  widgetId={value.id}
                  icon="setting"
                  deleteIcon="true"
                  children={
                     <LineChartModal
                        value={value}
                        data={listResponse}
                        allDataObjects={listResponseData}
                        loading={loading}
                     />
                  }
               />
            )
         }
         children={
            <>
               {checkIfWidgetHaveDataToRender(
                  chartList,
                  connectionsData,
                  value?.users,
                  itemFromConnection
               ) ? (
                  <LineChart
                     width={
                        //@ts-ignore
                        chartList?.length * 90 < width ? width - 340 : chartList?.length * 90
                     }
                     height={500}
                     data={fillUndefinedWithRandomNumbers(
                        sumRatings ? sumedWidgetDataList : chartList
                     )}
                     margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5
                     }}
                  >
                     <CartesianGrid strokeDasharray="3 3" />
                     <XAxis dataKey="name" />
                     <ReferenceLine
                        y={0}
                        stroke="black"
                        strokeDasharray="0"
                        label={{ fill: "black" }}
                     >
                        <Label value={0} position="left" dy={0} />
                     </ReferenceLine>

                     <YAxis domain={[axisYValues?.maxYValue, axisYValues?.minYValue]}></YAxis>

                     <Tooltip />
                     <Legend />

                     {itemsList?.map((user) => {
                        return (
                           <Line type="monotone" dataKey={user.label} stroke={user.userColor} />
                        );
                     })}
                  </LineChart>
               ) : (
                  <Spin />
               )}
            </>
         }
      ></Widget>
   );
};

export default LineChartWidget;
